import styled from 'foundations-theming/utils';
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { UpIcon } from 'foundations-assets';
import { DownIcon } from 'foundations-assets';
import { Button } from '../Button/Button';
import { callIfPossible } from '../../utils/functions';
import { mergeRefs } from '../../utils/mergeRefs/mergeRefs';
import { Controllable } from '../Controllable/Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { lazyEval } from '../../utils/lazyEval';
import { Overhang as DefaultOverhang } from '../Overhang/Overhang';
import { requestSchedulerCallback, cancelSchedulerCallback } from '../../utils/Timers';
import { KeyboardModeContext } from '../../../components/AppProvider/KeyboardModeProvider';
import { withTokenProps } from '../../../utils/withTokenProps';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const EXPANDABLE_TEXT_HEIGHT_OFFSET = 40;
const getStyle = computedMaxHeight => ({
  maxHeight: computedMaxHeight
});

// If a rems value exists, convert it to px and return it; otherwise, return the px value.
const pxValue = (px, rems) => rems != null ? rems * 16 : px;
const TEXT_WRAPPER_BORDER_RADIUS_TOKEN = 'transitional-container';
const StyledContainer = styled.div.withConfig({
  displayName: "ExpandableText__StyledContainer",
  componentId: "sc-1y298xl-0"
})(({
  $hasBottomToggleButton
}) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 150ms ease-out',
    position: !$hasBottomToggleButton ? 'relative' : 'static'
  };
});
const StyledButtonWrapper = styled.div.withConfig({
  displayName: "ExpandableText__StyledButtonWrapper",
  componentId: "sc-1y298xl-1"
})(({
  $buttonAlign,
  $scrollable
}) => {
  return Object.assign({
    alignSelf: $buttonAlign === 'center' ? 'center' : 'auto'
  }, $buttonAlign === 'left' && !$scrollable && {
    alignSelf: 'flex-start'
  }, $scrollable && {
    alignSelf: 'flex-end'
  });
});
const StyledTextWrapper = styled.div.withConfig({
  displayName: "ExpandableText__StyledTextWrapper",
  componentId: "sc-1y298xl-2"
})(({
  $hasBottomToggleButton,
  $scrollable,
  $cssStyles,
  theme
}) => {
  const computedScrollable = {
    border: `${theme.borderWidth['100']} solid ${theme.color['border-transitional-core-subtle']}`,
    borderRadius: theme.borderRadius[TEXT_WRAPPER_BORDER_RADIUS_TOKEN],
    overflowX: 'auto',
    overflowY: 'scroll',
    padding: `${theme.space['400']} ${theme.space['500']}`
  };
  return Object.assign({
    overflow: 'hidden',
    position: $hasBottomToggleButton ? 'relative' : 'static'
  }, $scrollable ? computedScrollable : {}, $cssStyles);
});
export class ExpandableTextComponent extends Component {
  constructor(props) {
    super(props);
    this._el = null;
    this._timeout = null;
    this._containerEl = null;
    this._observer = null;
    this.delayedFitToMaxLines = () => {
      if (this._timeout) {
        cancelSchedulerCallback(this._timeout);
      }
      this._timeout = requestSchedulerCallback(this.fitToMaxLines);
    };
    this.getButtonHeight = buttonEl => {
      if (!buttonEl) return 0;
      const styles = window.getComputedStyle(buttonEl);
      const margin = parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);
      return Math.ceil(buttonEl.offsetHeight + margin);
    };
    this.fitToMaxLines = () => {
      if (!this._el) return;
      if (!this._containerEl) return;
      const {
        maxHeight = 120,
        maxHeightRems
      } = this.props;
      const {
        collapsible,
        contentHeight
      } = this.state;
      const buttonHeight = this.getButtonHeight(this._containerEl.children[1]);
      const scrollHeight = this._el.scrollHeight + buttonHeight;
      if (contentHeight !== scrollHeight) {
        this.setState({
          contentHeight: scrollHeight
        });
      }
      const contentOverflows = scrollHeight > pxValue(maxHeight, maxHeightRems);
      if (collapsible !== contentOverflows) {
        this.setState({
          collapsible: contentOverflows
        });
      }
    };
    this.handleExpandButtonClick = evt => {
      const {
        onExpandButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onExpandButtonClick, evt);
      callIfPossible(onExpandedChange, syntheticEvent(true));
    };
    this.handleCollapseButtonClick = evt => {
      const {
        onCollapseButtonClick,
        onExpandedChange
      } = this.props;
      callIfPossible(onCollapseButtonClick, evt);

      // Set explicit max-height (removed by handleTransitionEnd) for transition
      if (this._containerEl) {
        const textContainerEl = this._containerEl;
        textContainerEl.style.maxHeight = `${textContainerEl.scrollHeight}px`;
      }
      callIfPossible(onExpandedChange, syntheticEvent(false));
    };
    this.handleTextWrapperFocus = (evt, isKeyboardMode) => {
      const {
        expandOnFocus = true,
        onExpandedChange,
        toggleable = true
      } = this.props;
      if (expandOnFocus && toggleable) {
        // Avoid expanding on focus if the user clicked a link or other extraneous element.
        if (!isKeyboardMode && !/TEXTAREA|INPUT/.test(evt.target.tagName)) return;
        callIfPossible(onExpandedChange, syntheticEvent(true));
      }
    };
    this.handleTextWrapperTransitionEnd = evt => {
      // After the expand transition, remove max-height in case the content grows/shrinks
      const textWrapperEl = this._el;
      if (evt.target === textWrapperEl && evt.propertyName === 'max-height') {
        const {
          expanded,
          fullyExpanded
        } = this.state;
        if (expanded && !fullyExpanded) {
          this.setState({
            fullyExpanded: true
          });
        }
      }
    };
    this.textWrapperRefCallback = ref => {
      this._el = ref;
    };
    this.textContainerRefCallback = ref => {
      this._containerEl = ref;
    };
    this.state = {
      contentHeight: null,
      // How tall is the content?
      collapsible: false,
      // Does the content exceed our max height?
      fullyExpanded: false // Did the CSS transition from collapsed to expanded complete?
    };
    this._getStyle = memoizeOne(getStyle);
  }
  componentDidMount() {
    this.delayedFitToMaxLines();
    this.startMutationObserver();
    window.addEventListener('resize', this.delayedFitToMaxLines);
    if (this._el) this._el.addEventListener('load', this.delayedFitToMaxLines, true); // #5980: need to add this handler manually
    // if (!fontsLoaded()) fontsLoadedPromise.then(this.delayedFitToMaxLines);
  }
  componentDidUpdate(prevProps, prevState) {
    this.stopMutationObserver();

    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.contentHeight === this.state.contentHeight && prevState.collapsible === this.state.collapsible) {
      this.delayedFitToMaxLines();
    }
    if (prevState.collapsible !== this.state.collapsible) {
      callIfPossible(this.props.onCollapsibleChange, this.state.collapsible);
    }
    this.startMutationObserver();
  }
  componentWillUnmount() {
    this.stopMutationObserver();
    if (this._timeout) {
      cancelSchedulerCallback(this._timeout);
    }
    window.removeEventListener('resize', this.delayedFitToMaxLines);
    if (this._el && this._el.removeEventListener) this._el.removeEventListener('load', this.delayedFitToMaxLines, true);
  }
  startMutationObserver() {
    if (!this._el) return;

    // When the content changes, recalculate our size
    this._observer = new MutationObserver(this.delayedFitToMaxLines);
    this._observer.observe(this._el, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  stopMutationObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }
  render() {
    const {
      buttonAlign = 'center',
      children,
      collapseButtonText = I18n.text('salesUI.UIExpandableText.collapseButtonText'),
      cssStyles,
      domNodeRef,
      expandButtonText = I18n.text('salesUI.UIExpandableText.expandButtonText'),
      expanded = false,
      fadeOutHeight = 32,
      fadeOutHeightRems,
      gradientColor,
      hideButtonCaret = false,
      maxHeight = 120,
      maxHeightRems,
      onCollapseButtonClick,
      onExpandButtonClick,
      Overhang = DefaultOverhang,
      scrollable = false,
      toggleable = true,
      use = 'default'
    } = this.props;
    const {
      collapsible,
      contentHeight,
      fullyExpanded
    } = this.state;
    const {
      isKeyboardMode
    } = this.context;
    const showToggleButton = toggleable && collapsible;
    const hasBottomToggleButton = !scrollable && showToggleButton;
    const computedMaxHeight = expanded ? contentHeight : pxValue(maxHeight, maxHeightRems);
    const lazyEvalText = expanded ? collapseButtonText : expandButtonText;
    const renderedButton = showToggleButton ? /*#__PURE__*/_jsx(StyledButtonWrapper, {
      $buttonAlign: buttonAlign,
      $scrollable: scrollable,
      children: /*#__PURE__*/_jsxs(Button, {
        "aria-hidden": expanded ? !onCollapseButtonClick : !onExpandButtonClick,
        onClick: expanded ? this.handleCollapseButtonClick : this.handleExpandButtonClick,
        use: "link",
        children: [scrollable || hideButtonCaret ? null : expanded ? /*#__PURE__*/_jsx(UpIcon, {
          color: "icon-core-subtle"
        }) : /*#__PURE__*/_jsx(DownIcon, {
          color: "icon-core-subtle"
        }), lazyEval(lazyEvalText)]
      })
    }) : null;
    const defaultGradientColor = use === 'on-dark' ? 'fill-surface-raised' : 'fill-surface-default';
    return /*#__PURE__*/_jsxs(StyledContainer, {
      $hasBottomToggleButton: hasBottomToggleButton,
      "data-collapsible": collapsible,
      ref: mergeRefs([this.textContainerRefCallback, domNodeRef]),
      style: fullyExpanded ? {} : this._getStyle(computedMaxHeight),
      children: [scrollable && renderedButton, /*#__PURE__*/_jsxs(StyledTextWrapper, {
        $hasBottomToggleButton: hasBottomToggleButton,
        $scrollable: scrollable,
        onFocus: e => this.handleTextWrapperFocus(e, isKeyboardMode),
        onTransitionEnd: this.handleTextWrapperTransitionEnd,
        ref: this.textWrapperRefCallback,
        $cssStyles: cssStyles,
        children: [children, collapsible && !expanded ? /*#__PURE__*/_jsx(Overhang, {
          gradientColor: gradientColor != null ? gradientColor : defaultGradientColor,
          insetInlineEnd: scrollable ? '25' : '0',
          insetBlockEnd: scrollable ? '25' : '0',
          insetInlineStart: scrollable ? '25' : '0',
          side: "bottom",
          size: pxValue(fadeOutHeight, fadeOutHeightRems),
          borderEndEndRadius: TEXT_WRAPPER_BORDER_RADIUS_TOKEN // Ensures that the overhang doesn't cut off the bottom of the text wrapper border
          ,
          borderEndStartRadius: TEXT_WRAPPER_BORDER_RADIUS_TOKEN // Ensures that the overhang doesn't cut off the bottom of the text wrapper border
        }) : null]
      }), !scrollable && renderedButton]
    });
  }
}
ExpandableTextComponent.contextType = KeyboardModeContext;
export const ExpandableText = Controllable(withTokenProps(ExpandableTextComponent), ['expanded']);